/**
 * Sidebar registrations + sleeves loader for the authenticated layout.
 *
 * Extracted from layouts/default.vue (was ~245 lines of inline logic).
 * Responsibilities:
 *   - Validate / dehydrate the persisted tabSession cache so a stale or
 *     malformed entry can't paint the navigation with bogus data.
 *   - Apply the cached value immediately (synchronous paint) and only
 *     fall back to the network when there's no usable cache hit.
 *   - Refresh both collections whenever the active household changes.
 *
 * The composable owns its own reactive state — callers just read
 * `registrations`, `sleeves`, `isLoading`, and `error`. Watch on the
 * household id is set up internally so consumers don't have to wire it.
 * `isLoading` drives the sidebar's skeleton (see nav/RegsAndSleevesSkeleton).
 */
import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { useTabSessionStore } from '~/stores/tabSession'
import { readSidebarCacheValue } from '~/utils/sidebarCache'
import type { Registration, Sleeve, SleevesPerReg } from '~/types'

type SidebarListResponse<T> = { items: T[] }
type SidebarLoadStatus = 'idle' | 'pending' | 'success' | 'error'

function isRegistration(value: unknown): value is Registration {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return typeof candidate.id === 'string'
    && typeof candidate.accountNumber === 'string'
    && typeof candidate.name === 'string'
    && typeof candidate.value === 'number'
}

function isSleeve(value: unknown): value is Sleeve {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return typeof candidate.id === 'string'
    && typeof candidate.name === 'string'
    && typeof candidate.value === 'string'
    && typeof candidate.accountNumber === 'string'
}

function isRegistrationsList(value: unknown): value is Registration[] {
  return Array.isArray(value) && value.every(isRegistration)
}

function isSleevesPerRegList(value: unknown): value is SleevesPerReg[] {
  if (!Array.isArray(value)) return false
  return value.every((item) => {
    if (!item || typeof item !== 'object') return false
    const candidate = item as Record<string, unknown>
    return typeof candidate.registrationId === 'number'
      && Array.isArray(candidate.sleeves)
      && candidate.sleeves.every(isSleeve)
  })
}

export function useSidebarData(householdId: Ref<number>) {
  const tabSession = useTabSessionStore()
  const page = ref(1)
  const pageSize = ref(2000)

  const sidebarRegistrations = ref<Registration[]>([])
  const sidebarSleeves = ref<SleevesPerReg[]>([])
  const sidebarRegistrationsStatus = ref<SidebarLoadStatus>('idle')
  const sidebarSleevesStatus = ref<SidebarLoadStatus>('idle')
  const sidebarRegistrationsError = ref<unknown>(null)
  const sidebarSleevesError = ref<unknown>(null)

  async function fetchSidebarRegistrations(id: number) {
    const response = await $fetch<SidebarListResponse<Registration>>('/api/ui/registrations', {
      query: {
        householdId: id,
        includeHoldingPerc: false,
        page: page.value,
        pageSize: pageSize.value
      }
    })
    return response?.items ?? []
  }

  async function fetchSidebarSleeves(id: number) {
    const response = await $fetch<SidebarListResponse<SleevesPerReg>>('/api/ui/sleeves', {
      query: {
        householdId: id,
        includeHoldingPerc: false,
        groupedByReg: true,
        page: page.value,
        pageSize: pageSize.value
      }
    })
    return response?.items ?? []
  }

  async function loadSidebarCollection<T>({
    validate,
    fetcher,
    target,
    status,
    error,
    getCacheEntry,
    setCacheEntry,
    removeCacheEntry
  }: {
    validate: (value: unknown) => value is T[]
    fetcher: () => Promise<T[]>
    target: Ref<T[]>
    status: Ref<SidebarLoadStatus>
    error: Ref<unknown>
    getCacheEntry: () => unknown
    setCacheEntry: (value: T[]) => void
    removeCacheEntry: () => void
  }) {
    if (!import.meta.client) return

    // Cached sidebar rows are applied before any API work so page refreshes can
    // paint the left navigation immediately. That removes one visible wait from
    // the registration and sleeve page boot path.
    const cached = readSidebarCacheValue(getCacheEntry(), validate)
    if (cached) {
      target.value = cached
      status.value = 'success'
    } else {
      // Invalid, malformed, or expired sidebar cache entries are removed
      // here so the persisted tab session cannot keep serving stale or
      // broken navigation data.
      removeCacheEntry()
    }

    if (cached) return

    status.value = 'pending'
    error.value = null

    try {
      const freshData = await fetcher()
      target.value = freshData
      status.value = 'success'
      // Every successful sidebar fetch refreshes the persisted
      // sessionStorage entry inside `tabSession`, so later refreshes in
      // this tab can render fast.
      setCacheEntry(freshData)
    } catch (err) {
      error.value = err
      status.value = 'error'
    }
  }

  async function loadSidebarRegistrations(id: number) {
    await loadSidebarCollection({
      validate: isRegistrationsList,
      fetcher: () => fetchSidebarRegistrations(id),
      target: sidebarRegistrations,
      status: sidebarRegistrationsStatus,
      error: sidebarRegistrationsError,
      getCacheEntry: () => tabSession.getSidebarRegistrationsCacheEntry(id),
      setCacheEntry: value => tabSession.setSidebarRegistrationsCache(id, value),
      removeCacheEntry: () => tabSession.removeSidebarRegistrationsCache(id)
    })
  }

  async function loadSidebarSleeves(id: number) {
    await loadSidebarCollection({
      validate: isSleevesPerRegList,
      fetcher: () => fetchSidebarSleeves(id),
      target: sidebarSleeves,
      status: sidebarSleevesStatus,
      error: sidebarSleevesError,
      getCacheEntry: () => tabSession.getSidebarSleevesCacheEntry(id),
      setCacheEntry: value => tabSession.setSidebarSleevesCache(id, value),
      removeCacheEntry: () => tabSession.removeSidebarSleevesCache(id)
    })
  }

  function clearSidebarData() {
    sidebarRegistrations.value = []
    sidebarSleeves.value = []
    sidebarRegistrationsStatus.value = 'idle'
    sidebarSleevesStatus.value = 'idle'
    sidebarRegistrationsError.value = null
    sidebarSleevesError.value = null
  }

  const stop = watch(householdId, async (id, previousId) => {
    if (!id) {
      clearSidebarData()
      return
    }

    page.value = 1

    if (previousId && previousId !== id) {
      // Clear the in-memory arrays when the user switches households so the
      // sidebar cannot momentarily render rows from the previous household.
      clearSidebarData()
    }

    await Promise.all([
      loadSidebarRegistrations(id),
      loadSidebarSleeves(id)
    ])
  }, { immediate: true })

  onBeforeUnmount(() => stop())

  return {
    registrations: computed(() => sidebarRegistrations.value),
    sleeves: computed(() => sidebarSleeves.value),
    // True while EITHER collection is in flight. Sleeves are included on
    // purpose even though the rows are driven by registrations: each row's
    // footer reads "Show N Sleeves", so releasing the list as soon as the
    // registrations land would paint "Show 0 Sleeves" on every row and then
    // flip the numbers a moment later. Waiting for both costs nothing (they
    // are fetched in parallel) and removes that visible correction.
    //
    // A warm sessionStorage cache sets both statuses to 'success'
    // synchronously, so a cached sidebar never flashes a skeleton.
    isLoading: computed(() =>
      sidebarRegistrationsStatus.value === 'pending'
      || sidebarSleevesStatus.value === 'pending'
    ),
    error: computed(() => sidebarRegistrationsError.value || sidebarSleevesError.value)
  }
}
